import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { COLORS, FONTS, SPRING } from "./constants";
import { GridBackground } from "../EAKCArchitecture/components/GridBackground";

// Realistic EAKC query from actual usage
const QUERY_TEXT =
  "What is the EAKC platform and what are the latest context for 4th of April 2026 and 31st of March 2026? Also explain the code flow of RAG implementation.";

// Sidebar sessions
const SIDEBAR_SESSIONS = {
  today: ["EAKC Platform RAG Code Flow", "Today's Context Brief", "Goal of EAKC"],
  yesterday: ["2026 March 31 Session", "Chunk Quality Redesign"],
};

// Mode tabs at the bottom
const MODE_TABS = ["Chat", "Draft Email", "Research", "Battle Card", "Meeting Prep", "Sequence"];

export const Scene2QueryInput: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const titleAppear = spring({ frame, fps, config: SPRING.snappy });

  const exitOpacity = interpolate(frame, [210, 240], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  // Sidebar slide in
  const sidebarAppear = spring({ frame: frame - 5, fps, config: SPRING.gentle });

  // Chat area
  const chatAppear = spring({ frame: frame - 15, fps, config: SPRING.gentle });

  // Typing animation
  const charsVisible = Math.min(
    QUERY_TEXT.length,
    Math.floor(
      interpolate(frame, [30, 100], [0, QUERY_TEXT.length], {
        extrapolateLeft: "clamp",
        extrapolateRight: "clamp",
      }),
    ),
  );
  const displayedQuery = QUERY_TEXT.slice(0, charsVisible);
  const showCursor = frame % 20 < 12 && frame < 105;

  // Query-understanding stages (appear after query is typed):
  // intent classification/routing → acronym expansion (real pipeline stages 2-3).
  const checks = [
    { label: "Intent Classification", sublabel: "Routed → knowledge retrieval", icon: "🧭", delay: 110 },
    { label: "Acronym Expansion", sublabel: "EAKC, RAG → full terms", icon: "🔤", delay: 132 },
    { label: "Query Understood", sublabel: "Ready to rewrite & decompose", icon: "✅", delay: 154 },
  ];

  // All-clear
  const allClearAppear = spring({ frame: frame - 175, fps, config: SPRING.bouncy });

  return (
    <AbsoluteFill style={{ opacity: exitOpacity }}>
      <GridBackground opacity={0.04} />

      {/* Title */}
      <div
        style={{
          position: "absolute",
          top: 30,
          left: "50%",
          transform: "translate(-50%, 0)",
          textAlign: "center",
          opacity: interpolate(titleAppear, [0, 1], [0, 1]),
          zIndex: 10,
        }}
      >
        <div style={{ fontSize: 36, fontFamily: FONTS.heading, fontWeight: 700, color: COLORS.textPrimary }}>
          Step 1:{" "}
          <span style={{ color: COLORS.accentBlue }}>Query, Intent & Acronyms</span>
        </div>
      </div>

      {/* === EAKC UI Mockup === */}
      <div
        style={{
          position: "absolute",
          top: 100,
          left: 80,
          right: 80,
          bottom: 60,
          borderRadius: 16,
          background: "#0D1321",
          border: `1px solid ${COLORS.borderLight}`,
          overflow: "hidden",
          display: "flex",
        }}
      >
        {/* Left Sidebar */}
        <div
          style={{
            width: 240,
            borderRight: `1px solid ${COLORS.border}`,
            padding: "16px 12px",
            opacity: interpolate(sidebarAppear, [0, 1], [0, 1]),
            transform: `translateX(${interpolate(sidebarAppear, [0, 1], [-20, 0])}px)`,
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          {/* Logo */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 12px",
              marginBottom: 8,
            }}
          >
            <span style={{ fontSize: 18 }}>📋</span>
            <span style={{ fontFamily: FONTS.heading, fontSize: 16, fontWeight: 700, color: COLORS.textPrimary }}>
              EAKC Chat
            </span>
          </div>

          {/* New Chat button */}
          <div
            style={{
              padding: "10px 16px",
              borderRadius: 10,
              background: `${COLORS.accentBlue}20`,
              border: `1px solid ${COLORS.accentBlue}40`,
              fontFamily: FONTS.body,
              fontSize: 13,
              color: COLORS.accentBlue,
              textAlign: "center",
              fontWeight: 600,
              marginBottom: 12,
            }}
          >
            + New Chat
          </div>

          {/* Nav buttons row */}
          <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
            {["Upload", "Graph", "Docs", "Admin"].map((nav) => (
              <div
                key={nav}
                style={{
                  padding: "6px 14px",
                  borderRadius: 8,
                  background: COLORS.bgCard,
                  border: `1px solid ${COLORS.border}`,
                  fontFamily: FONTS.mono,
                  fontSize: 11,
                  color: COLORS.textMuted,
                }}
              >
                {nav}
              </div>
            ))}
          </div>

          {/* Sessions */}
          <div style={{ fontFamily: FONTS.mono, fontSize: 10, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>
            Today
          </div>
          {SIDEBAR_SESSIONS.today.map((session, i) => (
            <div
              key={i}
              style={{
                padding: "8px 12px",
                borderRadius: 8,
                background: i === 0 ? `${COLORS.accentBlue}15` : "transparent",
                border: i === 0 ? `1px solid ${COLORS.accentBlue}30` : "1px solid transparent",
                fontFamily: FONTS.body,
                fontSize: 12,
                color: i === 0 ? COLORS.accentBlue : COLORS.textSecondary,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {session}
            </div>
          ))}

          <div style={{ fontFamily: FONTS.mono, fontSize: 10, color: COLORS.textMuted, textTransform: "uppercase", letterSpacing: 1, marginTop: 12, marginBottom: 6 }}>
            Yesterday
          </div>
          {SIDEBAR_SESSIONS.yesterday.map((session, i) => (
            <div
              key={i}
              style={{
                padding: "8px 12px",
                borderRadius: 8,
                fontFamily: FONTS.body,
                fontSize: 12,
                color: COLORS.textSecondary,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {session}
            </div>
          ))}
        </div>

        {/* Main Chat Area */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            opacity: interpolate(chatAppear, [0, 1], [0, 1]),
          }}
        >
          {/* Chat header */}
          <div
            style={{
              padding: "12px 24px",
              borderBottom: `1px solid ${COLORS.border}`,
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <span style={{ fontSize: 14 }}>📋</span>
            <span style={{ fontFamily: FONTS.body, fontSize: 14, color: COLORS.textPrimary, fontWeight: 500 }}>
              EAKC Platform RAG Code Flow
            </span>
            <div style={{ marginLeft: "auto", display: "flex", gap: 12 }}>
              <span style={{ fontFamily: FONTS.mono, fontSize: 12, color: COLORS.accentGreen, padding: "4px 12px", borderRadius: 6, background: `${COLORS.accentGreen}15`, border: `1px solid ${COLORS.accentGreen}30` }}>
                Sources
              </span>
              <span style={{ fontFamily: FONTS.mono, fontSize: 12, color: COLORS.textMuted }}>Share</span>
              <span style={{ fontFamily: FONTS.mono, fontSize: 12, color: COLORS.textMuted }}>Export</span>
            </div>
          </div>

          {/* Chat messages */}
          <div style={{ flex: 1, padding: "24px 32px", overflowY: "hidden" }}>
            {/* User message bubble */}
            <div
              style={{
                maxWidth: 700,
                marginLeft: "auto",
                padding: "18px 22px",
                borderRadius: 14,
                background: `${COLORS.accentBlue}15`,
                border: `1px solid ${COLORS.accentBlue}25`,
                fontFamily: FONTS.body,
                fontSize: 15,
                color: COLORS.textPrimary,
                lineHeight: 1.7,
                marginBottom: 20,
              }}
            >
              {displayedQuery}
              {showCursor && <span style={{ color: COLORS.accentBlue }}>|</span>}
            </div>

            {/* Timestamp */}
            {charsVisible >= QUERY_TEXT.length && (
              <div
                style={{
                  textAlign: "right",
                  fontFamily: FONTS.mono,
                  fontSize: 11,
                  color: COLORS.textMuted,
                  marginBottom: 8,
                }}
              >
                12:05:57 AM
              </div>
            )}
          </div>

          {/* Bottom input bar */}
          <div style={{ padding: "12px 24px", borderTop: `1px solid ${COLORS.border}` }}>
            {/* Mode tabs */}
            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              {MODE_TABS.map((tab, i) => (
                <div
                  key={i}
                  style={{
                    padding: "5px 14px",
                    borderRadius: 16,
                    background: i === 0 ? COLORS.accentBlue : COLORS.bgCard,
                    fontFamily: FONTS.body,
                    fontSize: 11,
                    color: i === 0 ? "#fff" : COLORS.textMuted,
                    fontWeight: i === 0 ? 600 : 400,
                  }}
                >
                  {tab}
                </div>
              ))}
            </div>

            {/* Input */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "12px 18px",
                borderRadius: 12,
                background: COLORS.bgCard,
                border: `1px solid ${COLORS.borderLight}`,
              }}
            >
              <span style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.textMuted, flex: 1 }}>
                Ask a question about your knowledge base...
              </span>
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  background: COLORS.accentBlue,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 14,
                }}
              >
                ➤
              </div>
            </div>

            {/* Token counter */}
            <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 8 }}>
              <span style={{ fontFamily: FONTS.mono, fontSize: 10, color: COLORS.textMuted }}>
                1,784,042 tokens left
              </span>
              <span style={{ fontFamily: FONTS.mono, fontSize: 10, color: COLORS.textMuted }}>
                3,215,958 / 5,000,000
              </span>
              {/* Model badges */}
              <div style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
                {["Fast", "Balanced", "Deep"].map((m, i) => (
                  <span
                    key={i}
                    style={{
                      padding: "2px 8px",
                      borderRadius: 4,
                      background: i === 2 ? `${COLORS.accentRed}30` : COLORS.bgCard,
                      fontFamily: FONTS.mono,
                      fontSize: 10,
                      color: i === 2 ? COLORS.accentRed : COLORS.textMuted,
                      fontWeight: i === 2 ? 600 : 400,
                    }}
                  >
                    {m}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Guardrail overlay — appears after query typed */}
      {frame > 108 && (
        <div
          style={{
            position: "absolute",
            right: 100,
            top: 200,
            width: 380,
            zIndex: 20,
          }}
        >
          {checks.map((check, i) => {
            const checkAppear = spring({ frame: frame - check.delay, fps, config: SPRING.snappy });
            const tickAppear = spring({ frame: frame - check.delay - 6, fps, config: { damping: 80, mass: 0.4 } });

            return (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "10px 16px",
                  marginBottom: 8,
                  borderRadius: 10,
                  background: `${COLORS.bg}F0`,
                  border: `1px solid ${COLORS.borderLight}`,
                  opacity: interpolate(checkAppear, [0, 1], [0, 1]),
                  transform: `translateX(${interpolate(checkAppear, [0, 1], [20, 0])}px)`,
                  backdropFilter: "blur(8px)",
                }}
              >
                <span style={{ fontSize: 18 }}>{check.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: FONTS.mono, fontSize: 13, fontWeight: 600, color: COLORS.textPrimary }}>
                    {check.label}
                  </div>
                  <div style={{ fontFamily: FONTS.body, fontSize: 11, color: COLORS.textMuted, marginTop: 1 }}>
                    {check.sublabel}
                  </div>
                </div>
                <div
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    background: `${COLORS.accentGreen}20`,
                    border: `1.5px solid ${COLORS.accentGreen}60`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transform: `scale(${interpolate(tickAppear, [0, 1], [0, 1])})`,
                    opacity: interpolate(tickAppear, [0, 1], [0, 1]),
                  }}
                >
                  <span style={{ color: COLORS.accentGreen, fontSize: 12, fontWeight: 700 }}>✓</span>
                </div>
              </div>
            );
          })}

          {/* All clear */}
          <div
            style={{
              marginTop: 8,
              padding: "10px 20px",
              borderRadius: 10,
              background: `${COLORS.accentGreen}12`,
              border: `1.5px solid ${COLORS.accentGreen}40`,
              fontFamily: FONTS.mono,
              fontSize: 14,
              fontWeight: 600,
              color: COLORS.accentGreen,
              textAlign: "center",
              transform: `scale(${interpolate(allClearAppear, [0, 1], [0.8, 1])})`,
              opacity: interpolate(allClearAppear, [0, 1], [0, 1]),
            }}
          >
            ✅ Proceeding to query rewrite
          </div>
        </div>
      )}
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
